import {NumberInput, TextInput} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {z} from 'zod';

import {defaultTo, isNotNil, not} from 'ramda';

import i18n from '@dms/i18n';
import {
  EitherQuantityOrError,
  MappedBasketItem,
  useBasketItemQuantity,
  useTenant,
} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {MaterialReturnBasketItem} from '../types/MaterialReturnBasketItem';

interface AfterSalesMaterialReturnBasketItemQuantityWithPriceProps<
  T extends MaterialReturnBasketItem,
> extends RequiredTestIdProps {
  item: MappedBasketItem<T>;
  handlingUnit: string | Nullish;
  onQuantityChange: (itemId: string, quantity: EitherQuantityOrError) => Promise<void>;
  hasPendingRequest: boolean;
}

export function AfterSalesMaterialReturnBasketItemQuantityWithPrice<
  T extends MaterialReturnBasketItem,
>(props: AfterSalesMaterialReturnBasketItemQuantityWithPriceProps<T>) {
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();

  const {quantity, quantityError, handleQuantityChange} = useBasketItemQuantity({
    itemId: props.item.id,
    currentQuantity: props.item.quantity,
    validationSchema: quantitySchema({
      minValue: props.item.minMaxQuantity?.minQuantity,
      maxValue: props.item.minMaxQuantity?.maxQuantity,
    }),
    onQuantityChange: props.onQuantityChange,
  });

  const dispensingUnit = defaultTo(1, props.item.dispensingUnit);

  const totalPrice = formatCurrency(
    props.item.totalPrice.withoutVat.amount ?? 0,
    props.item.totalPrice.withoutVat.currency ?? tenant.tenantCurrency,
    2
  );

  return (
    <HStack justify="space-between">
      <HStack spacing={2}>
        <Box width={29}>
          <NumberInput
            isDisabled={props.hasPendingRequest || not(props.item.quantityEditingAllowed)}
            isInvalid={isNotNil(quantityError)}
            isStepperVisible
            value={quantity}
            step={dispensingUnit}
            errorMessage={quantityError}
            decimalPlaces={4}
            onChange={handleQuantityChange}
            data-testid={suffixTestId('quantity', props)}
          />
        </Box>
        <Box width={11}>
          <TextInput
            value={props.handlingUnit ?? DEFAULT_HANDLING_UNIT}
            isDisabled
            data-testid={suffixTestId('unit', props)}
          />
        </Box>
      </HStack>

      <VStack align="flex-end">
        <Text size="xSmall" color="tertiary">
          {i18n.t('general.labels.sellingPrice')}
        </Text>
        <Text size="small" alternative data-testid={suffixTestId('price', props)}>
          {totalPrice}
        </Text>
      </VStack>
    </HStack>
  );
}

const DEFAULT_HANDLING_UNIT = 'pcs';

type QuantitySchema = {
  minValue: number | Nullish;
  maxValue: number | Nullish;
};

const quantitySchema = (props: QuantitySchema) => {
  let schema = z.number();

  if (isNotNil(props.minValue)) {
    schema = schema.min(props.minValue);
  }

  if (isNotNil(props.maxValue)) {
    schema = schema.max(props.maxValue);
  }

  return schema;
};
